import type { PaymentType } from '@lt/api/dist/types/payment';
import React from 'react';
import cn from 'classnames';
import { PAYMENT_METHOD_MIR_CARD } from 'src/store/payment/constants';
import { useTranslation } from '@lt/localization';
import { formatCurrency } from 'src/utils/stringUtils';
import type { PaymentMethod } from '@lt/api/dist/methods/orders/types/paymentMethods';
import { CurrencyFormat } from '@lt/components';
import { Clock } from '@lt/components/icons-react';
import { TPayLogo } from '@lt/components/icons-react/payment';
import type { PaymentGroup } from '../../types';
import SubmitAgreement from '../SubmitAgreement';
import styles from './expandedPaymentMethod.module.css';
import CardPaymentContainer from '../CardPaymentContainer';
import PaylatePayment from '../PaylatePayment/PaylatePayment';
import TbankCreditExpanded from '../TbankCreditExpanded';
import YandexPay from '../YandexPay';

type Props = {
  type: PaymentType;
  group: PaymentGroup;
  amount: number;
  additionalData:
    | Extract<PaymentMethod, { additionalData: unknown }>['additionalData']
    | null;
  hasMirPayment: boolean;
  onChange?: (value: PaymentType) => void;
};

const ExpandedPaymentMethod = (props: Props) => {
  const { type, group, amount, hasMirPayment, onChange, additionalData } =
    props;
  const { t } = useTranslation();
  switch (true) {
    case type === 'sbp':
      return (
        <div
          className={styles.termsAndButton}
          data-testid="SUBMIT_AGREEMENT_SBP"
        >
          <SubmitAgreement>
            <span>{t('checkout.payment.pay')}</span>{' '}
            <CurrencyFormat value={amount} />
          </SubmitAgreement>
        </div>
      );
    case type === 't_pay':
      return (
        <div className={styles.termsAndButton}>
          <SubmitAgreement>
            <div className={styles.buttonContainer}>
              <span>{t('checkout.payment.pay')}</span>{' '}
              <CurrencyFormat value={amount} />
              <TPayLogo className={styles.tpayIcon} />
            </div>
          </SubmitAgreement>
        </div>
      );
    case type === 'yandex_pay':
      return <YandexPay />;
    case type === 't_bank_credit':
      return <TbankCreditExpanded />;
    case type === 't_bank_credit_flex':
      return (
        <div className={styles.termsAndButton}>
          <SubmitAgreement>
            <span>{t('checkout.payment.pay')}</span>{' '}
            <CurrencyFormat value={amount} />
          </SubmitAgreement>
        </div>
      );
    case group === 'card':
      return (
        <div
          className={cn(styles.flexContainer)}
          data-testid="PAYMENT_CARD_CONTAINER"
        >
          <div className={styles.leftFlexItem}>
            <CardPaymentContainer
              showPaymentIcon={!type.includes(PAYMENT_METHOD_MIR_CARD)}
              TooltipFooter={
                <a
                  className={styles.scrollToMirLink}
                  href={`#${PAYMENT_METHOD_MIR_CARD}`}
                  onClick={() => onChange?.(PAYMENT_METHOD_MIR_CARD)}
                >
                  {t('checkout.payment.choosePaymentMethod')}
                </a>
              }
              validateMIRCard={
                hasMirPayment && !type.includes(PAYMENT_METHOD_MIR_CARD)
              }
            />
          </div>
          <div className={styles.rightFlexItem}>
            <SubmitAgreement>
              <CurrencyFormat
                prefix={t('checkout.payment.bookFor')}
                value={amount}
              />
            </SubmitAgreement>
          </div>
        </div>
      );
    case group === 'paylate':
      return (
        <div
          className={cn(styles.flexContainer)}
          data-testid="PAYLATE_PAYMENT_METHOD"
        >
          <div className={styles.leftFlexItem}>
            <PaylatePayment
              amount={amount}
              additionalData={
                additionalData && 'payToday' in additionalData
                  ? additionalData
                  : undefined
              }
            />
          </div>
          <div className={styles.rightFlexItem}>
            <SubmitAgreement>{t('checkout.payment.bookTour')}</SubmitAgreement>
          </div>
        </div>
      );
    case group === 'pay_later':
      return (
        <div
          className={cn(styles.flexContainer)}
          data-testid="PAY_LATER_PAYMENT_METHOD"
        >
          <div className={styles.rightFlexItem}>
            <SubmitAgreement>{t('checkout.payment.bookTour')}</SubmitAgreement>
            <div className={styles.paytimeBlock}>
              <Clock width={16} />
              <span>{t('checkout.payment.pay')}</span>{' '}
              <div className={styles.price}>{formatCurrency(amount || 0)}</div>{' '}
              <span>{t('checkout.payment.in24hours')}</span>
            </div>
          </div>
        </div>
      );

    default:
      return null;
  }
};

export default ExpandedPaymentMethod;
